import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BrandingTab } from "@/components/BrandingTab";
import { PartnersTab } from "@/components/admin/PartnersTab";
import { AttendanceTab } from "@/components/admin/AttendanceTab";
import { SignalsTab } from "@/components/admin/SignalsTab";
import { StaffAuditTab } from "@/components/admin/StaffAuditTab";
import { MentorsTab } from "@/components/admin/MentorsTab";
import { ApiKeysTab } from "@/components/admin/ApiKeysTab";
import { LiveSupportInbox } from "@/components/admin/LiveSupportInbox";
import { SendEmailCard } from "@/components/admin/SendEmailCard";
import { GuideTab } from "@/components/admin/GuideTab";
import { MentorSignalComposer } from "@/components/admin/MentorSignalComposer";
import { MentorRoom } from "@/components/admin/MentorRoom";
import { MentorClientChats } from "@/components/admin/MentorClientChats";
import { MentorStats } from "@/components/admin/MentorStats";
import { MaintenanceToggle } from "@/components/Maintenance";
import { GlobalBotAvatar } from "@/components/GlobalBotAvatar";
import { AdminPinGate, isAdminPinValid } from "@/components/AdminPinGate";
import { AdminCpiControl } from "@/components/AdminCpiControl";
import { AdminProtectionDefaults } from "@/components/admin/AdminProtectionDefaults";
import { MentorAccount } from "@/components/admin/MentorAccount";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  ShieldCheck, Users, Clock, DollarSign, Check, X, LogOut,
  ExternalLink, Loader2, Activity, LifeBuoy, Send, Trash2, MessageSquare,
  KeyRound, Eye, EyeOff, CheckCircle2, CircleAlert, Bell, Search, Ban, Mail, Paperclip,
} from "lucide-react";

function Stat({ icon: Icon, label, value, onClick, active }) {
  const clickable = typeof onClick === "function";
  return (
    <div onClick={onClick} data-testid={clickable ? `stat-${label.toLowerCase().replace(/\s+/g, "-")}` : undefined}
      className={`bg-[#0F0F0F] border p-3 transition-colors ${clickable ? "cursor-pointer hover:border-white/60" : ""} ${active ? "border-white" : "border-[#222]"}`}>
      <div className="flex items-center gap-1.5 text-[#888] mb-1.5"><Icon className="h-3.5 w-3.5" /><span className="font-mono-term text-[10px] uppercase tracking-widest">{label}</span></div>
      <div className="font-heading text-xl font-bold">{value}</div>
    </div>
  );
}

export default function Admin() {
  const { user, logout, playTransition, endTransition } = useAuth();
  const navigate = useNavigate();
  const isCEO = !!user?.is_ceo;
  const isMentor = user?.role === "mentor";
  const showMentorArea = isCEO || isMentor;
  const isPureMentor = isMentor && !isCEO;
  const staffTab = !isPureMentor;  // pure mentors see ONLY the mentor console
  const [unlocked, setUnlocked] = useState(() => isAdminPinValid());
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState(isMentor && !isCEO ? "composer" : "payments");
  const [grant, setGrant] = useState({});
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [config, setConfig] = useState(null);
  const [tokenInput, setTokenInput] = useState("");
  const [region, setRegion] = useState("new-york");
  const [showToken, setShowToken] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantPlan, setGrantPlan] = useState("");
  const [grantDays, setGrantDays] = useState("");
  const [bcast, setBcast] = useState({ title: "", message: "" });
  const [bcastBusy, setBcastBusy] = useState(false);
  const [member, setMember] = useState({ name: "", username: "", email: "", phone: "", password: "", confirm: "", plan_id: "elite-30", role: "client" });
  const [memberBusy, setMemberBusy] = useState(false);
  const [roleEmail, setRoleEmail] = useState("");
  const [roleValue, setRoleValue] = useState("manager");
  const [lookupEmail, setLookupEmail] = useState("");
  const [found, setFound] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [fPlan, setFPlan] = useState("");
  const [fDays, setFDays] = useState("");
  const [fRole, setFRole] = useState("");
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminName, setNewAdminName] = useState("");
  const [teamPositions, setTeamPositions] = useState([]);
  const [teamStaff, setTeamStaff] = useState([]);
  const [assignEmail, setAssignEmail] = useState("");
  const [assignTitle, setAssignTitle] = useState("");
  const [assignRole, setAssignRole] = useState("admin");
  const [acEmail, setAcEmail] = useState("");
  const [composeTo, setComposeTo] = useState("");

  const setTeamRole = async () => {
    if (!roleEmail.trim()) { toast.error("Enter the member email"); return; }
    setBusy("set-role");
    try {
      const { data } = await api.post("/admin/set-role", {
        email: roleEmail.trim().toLowerCase(), role: roleValue,
      });
      const label = roleValue === "client" ? "Client" : (roleValue === "admin" ? "Admin" : "Manager");
      toast.success(`${data.user?.email} is now ${label}`);
      setRoleEmail("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const createMember = async () => {
    const m = member;
    if (!m.name.trim() || !m.username.trim() || !m.email.trim() || !m.password) {
      toast.error("Full name, username, email and password are required"); return;
    }
    if (!/^[a-zA-Z]+$/.test(m.username)) { toast.error("Username must be letters only (no digits)"); return; }
    if (m.password !== m.confirm) { toast.error("Passwords do not match"); return; }
    setMemberBusy(true);
    try {
      const { data } = await api.post("/admin/create-member", {
        name: m.name.trim(), username: m.username.trim().toLowerCase(), email: m.email.trim().toLowerCase(),
        phone: m.phone.trim(), password: m.password, plan_id: m.plan_id, role: m.role,
      });
      const kind = m.role === "client" ? "Member" : (m.role === "admin" ? "Admin" : "Manager");
      toast.success(`${kind} created: ${data.user?.email} — active & ready to log in`);
      setMember({ name: "", username: "", email: "", phone: "", password: "", confirm: "", plan_id: "elite-30", role: "client" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setMemberBusy(false);
    }
  };

  const inviteMember = async () => {
    const m = member;
    if (!m.name.trim() || !m.email.trim()) {
      toast.error("Full name and email are required to send an invite"); return;
    }
    setMemberBusy(true);
    try {
      const { data } = await api.post("/admin/invite-member", {
        name: m.name.trim(), email: m.email.trim().toLowerCase(),
        plan_id: m.role === "client" ? m.plan_id : null, role: m.role,
      });
      toast.success(`Invite sent to ${data.email} — they'll set their own password.`);
      setMember({ name: "", username: "", email: "", phone: "", password: "", confirm: "", plan_id: "elite-30", role: "client" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setMemberBusy(false);
    }
  };

  const loadTeam = async () => {
    try {
      const { data } = await api.get("/admin/positions");
      setTeamPositions(data.positions || []);
      setTeamStaff(data.staff || []);
    } catch (e) { /* ignore */ }
  };

  const assignPosition = async () => {
    const email = assignEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    if (!assignTitle) { toast.error("Choose a position"); return; }
    setBusy("assign-position");
    try {
      await api.post("/admin/assign-position", { email, title: assignTitle, role: assignRole });
      toast.success(`${assignTitle} assigned to ${email}`);
      setAssignEmail(""); setAssignTitle(""); loadTeam(); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const createAdmin = async () => {
    const email = newAdminEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy("create-admin");
    try {
      const { data } = await api.post("/admin/create-admin", { email, name: newAdminName.trim() || null });
      toast.success(`Admin invite sent to ${data.email} — they'll set their own password.`);
      setNewAdminEmail(""); setNewAdminName(""); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const fReminder = async () => {
    if (!found?.email) return;
    setBusy("f-reminder");
    try {
      await api.post("/admin/send-reminder", { email: found.email });
      toast.success(`Reminder email sent to ${found.email}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };


  const verifyByEmail = async () => {
    if (!grantEmail.trim()) { toast.error("Enter the client email"); return; }
    setBusy("verify-email");
    try {
      await api.post("/admin/verify-by-email", { email: grantEmail.trim().toLowerCase() });
      toast.success(`${grantEmail} verified — they can log in now`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };


  const sendBroadcast = async () => {
    if (!bcast.title.trim() || !bcast.message.trim()) { toast.error("Title and message required"); return; }
    setBcastBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast", { title: bcast.title.trim(), message: bcast.message.trim() });
      toast.success(`Sent to ${data.recipients ?? 0} subscriber(s)`);
      setBcast({ title: "", message: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBcastBusy(false);
    }
  };

  const load = async () => {
    const [s, p, u, pl, t, c] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/payments"), api.get("/admin/users"),
      api.get("/plans"), api.get("/admin/tickets"), api.get("/admin/config"),
    ]);
    setStats(s.data); setPayments(p.data); setUsers(u.data); setPlans(pl.data); setTickets(t.data);
    setConfig(c.data); setRegion(c.data?.metaapi_region || "new-york");
  };
  useEffect(() => { if (unlocked && !isPureMentor) load().catch(() => {}); }, [unlocked]);
  useEffect(() => { if (unlocked && !isPureMentor && tab === "team") loadTeam(); }, [unlocked, tab]);

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/admin/payments/${id}/${action}`);
      toast.success(`Payment ${action}d`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const setLicense = async (userId, planId) => {
    if (!planId) return;
    setBusy(userId);
    try {
      await api.post(`/admin/users/${userId}/license`, { plan_id: planId });
      toast.success("License granted");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const revoke = async (userId) => {
    setBusy(userId + "revoke");
    try { await api.post(`/admin/users/${userId}/suspend`); toast.success("Access revoked"); await load(); }
    finally { setBusy(null); }
  };

  const reactivate = async (userId) => {
    setBusy(userId + "react");
    try { await api.post(`/admin/users/${userId}/reactivate`); toast.success("Access reactivated"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const cancelRenewal = async (userId) => {
    setBusy(userId + "canr");
    try { await api.post(`/admin/users/${userId}/cancel-renewal`); toast.success("Renewal cancelled — plan runs until expiry"); await load(); }
    finally { setBusy(null); }
  };

  const enableRenewal = async (userId) => {
    setBusy(userId + "enr");
    try { await api.post(`/admin/users/${userId}/enable-renewal`); toast.success("Renewal re-enabled"); await load(); }
    finally { setBusy(null); }
  };

  const lookupUser = async (email) => {
    const e = (email ?? lookupEmail).trim();
    if (!e) { toast.error("Antre yon email"); return; }
    setLookupBusy(true);
    try {
      const { data } = await api.get("/admin/user-by-email", { params: { email: e } });
      setFound(data); setFRole(data.role || "client"); setFPlan(""); setFDays("");
    } catch (err) {
      setFound(null);
      toast.error(err.response?.status === 404 ? "Pa gen itilizatè ak email sa a" : formatApiError(err.response?.data?.detail));
    } finally { setLookupBusy(false); }
  };

  const refreshFound = async () => {
    if (found?.email) {
      try { const { data } = await api.get("/admin/user-by-email", { params: { email: found.email } }); setFound(data); setFRole(data.role || "client"); }
      catch { /* ignore */ }
    }
    await load();
  };

  const fGrant = async () => {
    if (!found || !fPlan) { toast.error("Chwazi yon plan"); return; }
    setBusy("f-grant");
    try {
      await api.post("/admin/grant-by-email", { email: found.email, plan_id: fPlan, days: fDays ? Number(fDays) : undefined });
      toast.success(`Aksè bay ${found.email}`); setFPlan(""); setFDays("");
      await refreshFound();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const fAction = async (kind) => {
    if (!found) return;
    const map = {
      revoke: `/admin/users/${found.id}/suspend`,
      reactivate: `/admin/users/${found.id}/reactivate`,
      "cancel-renewal": `/admin/users/${found.id}/cancel-renewal`,
      "enable-renewal": `/admin/users/${found.id}/enable-renewal`,
    };
    setBusy("f-" + kind);
    try {
      await api.post(map[kind]);
      toast.success({ revoke: "Aksè revoke", reactivate: "Aksè reyaktive", "cancel-renewal": "Renouvèlman anile", "enable-renewal": "Renouvèlman aktive" }[kind]);
      await refreshFound();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const fVerify = async () => {
    if (!found) return;
    setBusy("f-verify");
    try { await api.post("/admin/verify-by-email", { email: found.email.toLowerCase() }); toast.success("Verifye — li ka konekte kounye a"); await refreshFound(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const fSetRole = async () => {
    if (!found || !fRole) return;
    setBusy("f-role");
    try {
      await api.post("/admin/set-role", { email: found.email.toLowerCase(), role: fRole });
      toast.success(`Wòl mete: ${fRole === "client" ? "Client" : fRole === "admin" ? "Admin" : "Manager"}`);
      await refreshFound();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const fResend = async () => {
    if (!found) return;
    setBusy("f-resend");
    try {
      const { data } = await api.post("/admin/resend-credentials", { email: found.email.toLowerCase() });
      toast.success(data.password
        ? `Enfo voye bay ${data.email}. Nouvo modpas: ${data.password}`
        : `Enfo koneksyon voye bay ${data.email}`);
      await refreshFound();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const fDelete = async () => {
    if (!found) return;
    if (!window.confirm(`Efase kont ${found.email} nèt? Sa ap retire tout done l yo (pèman, komisyon, kont). Aksyon sa a pa ka defèt.`)) return;
    setBusy("f-delete");
    try {
      const { data } = await api.delete(`/admin/users/${found.id}`);
      toast.success(`Kont ${data.deleted} efase nèt. Ou ka reyitilize imèl la kounye a.`);
      setFound(null); setLookupEmail("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const quickAccess = async (mode) => {
    const e = acEmail.trim();
    if (!e) { toast.error("Antre yon email"); return; }
    setBusy("ac-" + mode);
    try {
      const { data } = await api.get("/admin/user-by-email", { params: { email: e } });
      await api.post(`/admin/users/${data.id}/${mode === "revoke" ? "suspend" : "reactivate"}`);
      toast.success(mode === "revoke" ? `Aksè revoke pou ${data.email}` : `Aksè reyaktive pou ${data.email}`);
      await load();
      if (found?.email && found.email.toLowerCase() === data.email.toLowerCase()) await refreshFound();
    } catch (err) {
      toast.error(err.response?.status === 404 ? "Pa gen itilizatè ak email sa a" : formatApiError(err.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const staffSuspend = async (id, suspend) => {
    setBusy("staff-" + id);
    try {
      await api.post(`/admin/staff/${id}/${suspend ? "suspend" : "reactivate"}`);
      toast.success(suspend ? "Aksè staf la sispann" : "Aksè staf la reyaktive");
      loadTeam();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const doLogout = async () => {
    await playTransition("logout");
    await logout();
    navigate("/login");
    setTimeout(endTransition, 700);
  };
  const clients = users.filter((u) => u.role === "client");

  const resetTestData = async () => {
    if (!window.confirm("Rezèt TOUT done peman yo? Sa ap efase tout pèman, tranzaksyon Stripe ak komisyon pou dashboard lan kòmanse a $0. Kont itilizatè yo p ap efase. Aksyon sa a pa ka defèt.")) return;
    setBusy("reset-test");
    try {
      const { data } = await api.post("/admin/reset-test-data");
      const d = data.deleted || {};
      toast.success(`Done tès rezèt: ${d.payments || 0} pèman, ${d.commissions || 0} komisyon efase. Dashboard lan a $0 kounye a.`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setBusy("reply");
    try {
      await api.post(`/admin/tickets/${replyFor.id}/reply`, { reply: replyText.trim() });
      toast.success("Reply sent");
      setReplyFor(null); setReplyText("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const resolveTicket = async (id) => {
    setBusy(id + "resolve");
    try { await api.post(`/admin/tickets/${id}/resolve`); toast.success("Marked resolved"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const deleteTicket = async (id) => {
    setBusy(id + "delete");
    try { await api.delete(`/admin/tickets/${id}`); toast.success("Ticket deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const saveConfig = async () => {
    if (!tokenInput.trim()) { toast.error("Paste your MetaAPI token first"); return; }
    setBusy("config");
    try {
      await api.post("/admin/config", { metaapi_token: tokenInput.trim(), metaapi_region: region });
      toast.success("MetaAPI token saved securely");
      setTokenInput(""); setShowToken(false);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const clearConfig = async () => {
    setBusy("clearconfig");
    try { await api.delete("/admin/config/metaapi"); toast.success("MetaAPI token removed"); setTestResult(null); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const testConnection = async () => {
    setBusy("test"); setTestResult(null);
    try {
      const { data } = await api.post("/admin/config/test");
      setTestResult(data);
      toast.success(`Connected — ${data.count} MT5 account(s) found`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const grantByEmail = async () => {
    if (!grantEmail.trim() || !grantPlan) { toast.error("Enter client email and select a plan"); return; }
    setBusy("grant-email");
    try {
      const { data } = await api.post("/admin/grant-by-email", {
        email: grantEmail.trim(), plan_id: grantPlan, days: grantDays ? Number(grantDays) : undefined });
      toast.success(`Access granted to ${data.user.email}`);
      setGrantEmail(""); setGrantDays(""); setGrantPlan("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const revokeByEmail = async () => {
    if (!grantEmail.trim()) { toast.error("Enter the client email"); return; }
    setBusy("revoke-email");
    try {
      await api.post("/admin/revoke-by-email", { email: grantEmail.trim() });
      toast.success("Access revoked");
      setGrantEmail("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!unlocked) return <AdminPinGate onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-[#050505] grain">
      <header className="h-16 border-b border-[#222] bg-black/60 backdrop-blur-xl sticky top-0 z-30 flex items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-white flex items-center justify-center"><ShieldCheck className="h-5 w-5 text-black" /></div>
          <div>
            <div className="font-mono-term text-xs uppercase tracking-widest text-[#666]">{isPureMentor ? "Mentor Console" : "CEO Console"}</div>
            <div className="font-heading font-semibold text-sm">Smart FX {isPureMentor ? "Mentor" : "Admin"}</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/app")} className="h-9 rounded-sm text-[#ccc] hover:bg-[#1A1A1A]"><Activity className="mr-2 h-4 w-4" /> Dashboard</Button>
          <span className="text-sm text-[#888] hidden sm:block">{user?.email}</span>
          <Button variant="ghost" onClick={doLogout} data-testid="admin-logout" className="h-9 rounded-sm text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30]"><LogOut className="h-4 w-4" /></Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-5 fade-up">
        {!isPureMentor && (
          <>
            <h1 className="font-heading text-lg font-bold tracking-tight mb-3">Overview</h1>
            <div className="grid grid-cols-3 lg:grid-cols-4 gap-2.5 mb-5">
              <Stat icon={Users} label="Clients" value={stats?.total_users ?? "—"} onClick={() => setTab("users")} active={tab === "users"} />
              <Stat icon={ShieldCheck} label="Active Subs" value={stats?.active_subscriptions ?? "—"} onClick={() => setTab("users")} active={tab === "users"} />
              <Stat icon={Clock} label="Pending" value={stats?.pending_payments ?? "—"} onClick={() => setTab("payments")} active={tab === "payments"} />
              <Stat icon={LifeBuoy} label="Open Tickets" value={stats?.open_tickets ?? "—"} onClick={() => setTab("tickets")} active={tab === "tickets"} />
            </div>
            <MaintenanceToggle />
          </>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#0F0F0F] border border-[#222] rounded-sm flex-wrap h-auto">
            {staffTab && <TabsTrigger value="payments" data-testid="tab-payments" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black">Payments</TabsTrigger>}
            {staffTab && <TabsTrigger value="users" data-testid="tab-users" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black">Users</TabsTrigger>}
            {staffTab && <TabsTrigger value="access" data-testid="tab-access" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black">Grant Access</TabsTrigger>}
            {staffTab && <TabsTrigger value="create" data-testid="tab-create" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black">Create Member</TabsTrigger>}
            {staffTab && <TabsTrigger value="team" data-testid="tab-team" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black">Team</TabsTrigger>}
            <TabsTrigger value="attendance" data-testid="tab-attendance" className={`rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black ${staffTab ? "" : "hidden"}`}>Prezans</TabsTrigger>
            {staffTab && <TabsTrigger value="stafflog" data-testid="tab-stafflog" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black">Jounal Ekip</TabsTrigger>}
            {showMentorArea && <TabsTrigger value="mstats" data-testid="tab-mstats" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-[#00F593] data-[state=active]:text-black">My Stats</TabsTrigger>}
            {showMentorArea && <TabsTrigger value="composer" data-testid="tab-composer" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-[#00F593] data-[state=active]:text-black">Signal Composer</TabsTrigger>}
            {showMentorArea && <TabsTrigger value="mroom" data-testid="tab-mroom" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-[#00F593] data-[state=active]:text-black">Mentor Room</TabsTrigger>}
            {showMentorArea && <TabsTrigger value="mchats" data-testid="tab-mchats" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-[#00F593] data-[state=active]:text-black">Client Chats</TabsTrigger>}
            {showMentorArea && <TabsTrigger value="myaccount" data-testid="tab-myaccount" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-[#00F593] data-[state=active]:text-black">Kont Mwen</TabsTrigger>}
            {isCEO && <TabsTrigger value="mentors" data-testid="tab-mentors" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-[#00F593] data-[state=active]:text-black">Mentors</TabsTrigger>}
            {staffTab && <TabsTrigger value="signals" data-testid="tab-signals" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black">Signals</TabsTrigger>}
            {staffTab && <TabsTrigger value="partners" data-testid="tab-partners" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black">Partners</TabsTrigger>}
            <TabsTrigger value="tickets" data-testid="tab-tickets" className={`rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black ${staffTab ? "" : "hidden"}`}>
              Tickets{stats?.open_tickets ? <span className="ml-1.5 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-[#f5a623] text-black text-[10px] font-bold">{stats.open_tickets}</span> : null}
            </TabsTrigger>
            {staffTab && <TabsTrigger value="metaapi" data-testid="tab-metaapi" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black">MetaAPI</TabsTrigger>}
            {staffTab && <TabsTrigger value="notify" data-testid="tab-notify" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black">Notify</TabsTrigger>}
            {staffTab && <TabsTrigger value="cpi" data-testid="tab-cpi" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black">CPI Control</TabsTrigger>}
            {isCEO && <TabsTrigger value="protection" data-testid="tab-protection" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black">Pwoteksyon</TabsTrigger>}
            {staffTab && <TabsTrigger value="livesupport" data-testid="tab-livesupport" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-[#00F593] data-[state=active]:text-black">Sipò Ajan</TabsTrigger>}
            {staffTab && <TabsTrigger value="guide" data-testid="tab-guide" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-[#1C8CFF] data-[state=active]:text-white">Gid</TabsTrigger>}
            {staffTab && <TabsTrigger value="branding" data-testid="tab-branding" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-white data-[state=active]:text-black">Branding</TabsTrigger>}
            {isCEO && <TabsTrigger value="apikeys" data-testid="tab-apikeys" className="rounded-sm text-xs px-2.5 h-8 data-[state=active]:bg-[#f5a623] data-[state=active]:text-black">Kle API</TabsTrigger>}
          </TabsList>

          <TabsContent value="payments" className="mt-5">
            {isCEO && (
              <div className="mb-4 border border-[#FF3B30]/30 bg-[#0F0F0F] rounded-sm p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="reset-test-data-card">
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-sm text-[#FF3B30]">Rezèt Done Tès (Lansman)</div>
                  <p className="text-[11px] text-[#888] leading-relaxed mt-0.5">Efase tout pèman, tranzaksyon Stripe ak komisyon pou dashboard lan (revni) kòmanse a $0 anvan ou lanse pou vre. Kont itilizatè yo p ap efase.</p>
                </div>
                <Button onClick={resetTestData} data-testid="reset-test-data-button" disabled={busy === "reset-test"}
                  variant="ghost" className="h-10 rounded-sm text-[#FF3B30] border border-[#FF3B30]/40 hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] px-4 shrink-0">
                  {busy === "reset-test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1.5 h-4 w-4" /> Rezèt a $0</>}
                </Button>
              </div>
            )}
            <div className="border border-[#222] bg-[#0F0F0F]">
              {payments.filter((p) => p.status === "pending").length === 0 ? <div className="p-10 text-center text-[#888]">No pending payments.</div> :
                <Accordion type="multiple" className="w-full">
                  {payments.filter((p) => p.status === "pending").map((p) => (
                    <AccordionItem key={p.id} value={p.id} data-testid={`admin-payment-${p.id}`} className="border-[#222]">
                      <AccordionTrigger className="hover:no-underline px-4 sm:px-5 py-3.5">
                        <div className="min-w-0 pr-3">
                          <div className="font-heading font-semibold text-sm break-words text-left">{p.user_name} · {p.user_email}</div>
                          <div className="font-mono-term text-xs text-[#f5a623] mt-0.5 break-words text-left">{p.plan_name} · ${p.amount} · {p.days}d · {p.method || "BTC"}</div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 sm:px-5">
                        {p.txid && <div className="font-mono-term text-[11px] text-[#666] mb-3 break-all">tx {p.txid}</div>}
                        <div className="flex flex-wrap items-center gap-2">
                          {p.proof_image && <Button variant="outline" size="sm" onClick={() => setProof(p.proof_image)} className="h-9 rounded-sm border-[#333] bg-transparent hover:bg-[#1A1A1A]"><ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Proof</Button>}
                          <Button size="sm" onClick={() => act(p.id, "approve")} data-testid={`approve-${p.id}`} disabled={busy === p.id + "approve"} className="h-9 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] font-semibold"><Check className="mr-1 h-4 w-4" /> Approve</Button>
                          <Button size="sm" onClick={() => act(p.id, "reject")} data-testid={`reject-${p.id}`} disabled={busy === p.id + "reject"} variant="outline" className="h-9 rounded-sm border-[#FF3B30]/40 text-[#FF3B30] bg-transparent hover:bg-[#FF3B30]/10"><X className="mr-1 h-4 w-4" /> Reject</Button>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>}
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-5">
            <div className="mb-3 text-xs text-[#888] font-mono-term">Peze yon moun pou w ouvri opsyon l yo. Pou bay/revoke/renouvle oswa wòl, sèvi ak tab <span className="text-white">Grant Access</span> (rechèch pa email).</div>
            <div className="border border-[#222] bg-[#0F0F0F]">
              <Accordion type="multiple" className="w-full">
                {clients.map((c) => (
                  <AccordionItem key={c.id} value={c.id} data-testid={`admin-user-${c.id}`} className="border-[#222]">
                    <AccordionTrigger className="hover:no-underline px-4 sm:px-5 py-3.5">
                      <div className="min-w-0 pr-3">
                        <div className="font-heading font-semibold text-sm break-words text-left">{c.name} · {c.email}</div>
                        <div className={`font-mono-term text-xs mt-0.5 text-left ${c.license?.status === "suspended" ? "text-[#FFB020]" : c.license?.active ? "text-[#00F593]" : "text-[#888]"}`}>
                          {c.license?.status === "suspended"
                            ? `Suspended · ${c.license.plan_name || "plan"}`
                            : c.license?.active
                              ? `${c.license.plan_name} · ${c.license.days_left}d left${c.license.auto_renew === false ? " · renewal off" : ""}`
                              : c.license?.status === "expired" ? "Expired" : "No plan"}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 sm:px-5">
                      <Button size="sm" variant="ghost" onClick={() => { setLookupEmail(c.email); lookupUser(c.email); document.querySelector('[data-testid="tab-access"]')?.click(); }}
                        data-testid={`manage-user-${c.id}`} className="h-9 rounded-sm text-[#8fb4e6] hover:bg-[#8fb4e6]/10 text-xs gap-1.5">
                        <Search className="h-3.5 w-3.5" /> Jere aksè (Grant / Revoke / Renewal / Wòl)
                      </Button>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </TabsContent>

          <TabsContent value="access" className="mt-5">
            <div className="border border-[#3a1f2a] bg-[#0F0F0F] p-6 max-w-2xl space-y-4 mb-5" data-testid="quick-access-card">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-[#FF3B30] flex items-center justify-center"><Ban className="h-5 w-5 text-white" /></div>
                <div>
                  <div className="font-heading font-semibold">Revoke / Reyaktive Aksè</div>
                  <div className="font-mono-term text-xs text-[#888]">Bloke oswa remete aksè nenpòt moun rapidman — pa email</div>
                </div>
              </div>
              <p className="text-xs text-[#FF8f8f] leading-relaxed">
                Lè w revoke yon moun, li p ap ka fè ANYEN nan app la epi bot li ap kanpe otomatikman — jiskaske w reyaktive l. Plan an pa efase, se jis bloke.
              </p>
              <div>
                <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Email</label>
                <Input value={acEmail} onChange={(e) => setAcEmail(e.target.value)} data-testid="ac-email-input"
                  placeholder="moun@email.com" className="mt-2 bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => quickAccess("revoke")} data-testid="ac-revoke-button" disabled={busy === "ac-revoke"}
                  className="h-11 rounded-sm bg-[#FF3B30] text-white hover:bg-[#e02d23] font-semibold px-6 w-full sm:w-auto justify-center">
                  {busy === "ac-revoke" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Ban className="mr-1.5 h-4 w-4" /> Revoke Aksè</>}
                </Button>
                <Button onClick={() => quickAccess("reactivate")} data-testid="ac-reactivate-button" disabled={busy === "ac-reactivate"}
                  className="h-11 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] font-semibold px-6 w-full sm:w-auto justify-center">
                  {busy === "ac-reactivate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-1.5 h-4 w-4" /> Reyaktive Aksè</>}
                </Button>
              </div>
            </div>

            <div className="border border-[#222] bg-[#0F0F0F] p-6 max-w-2xl space-y-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-white flex items-center justify-center"><KeyRound className="h-5 w-5 text-black" /></div>
                <div>
                  <div className="font-heading font-semibold">Manage Access</div>
                  <div className="font-mono-term text-xs text-[#888]">Chèche yon moun pa email — bay plan, revoke, reyaktive, renouvèlman oswa wòl staf — tout nan yon sèl kote.</div>
                </div>
              </div>
              <div>
                <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Email</label>
                <div className="flex flex-col sm:flex-row gap-2 mt-2">
                  <Input value={lookupEmail} onChange={(e) => setLookupEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && lookupUser()} data-testid="lookup-email-input"
                    placeholder="moun@email.com" className="bg-[#0A0A0A] border-[#222] rounded-sm h-11 flex-1" />
                  <Button onClick={() => lookupUser()} data-testid="lookup-button" disabled={lookupBusy}
                    className="h-11 rounded-sm bg-white text-black hover:bg-[#e5e5e5] font-semibold px-6">
                    {lookupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="mr-1.5 h-4 w-4" /> Chèche</>}
                  </Button>
                </div>
              </div>

              {found && (
                <div data-testid="found-user-card" className="border border-[#222] bg-[#0A0A0A] rounded-sm p-4 space-y-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-heading font-semibold text-sm break-words">{found.name}</div>
                      <div className="font-mono-term text-xs text-[#888] break-words">{found.email}</div>
                    </div>
                    <span className={`font-mono-term text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${found.role === "client" ? "border-[#333] text-[#888]" : "border-[#0057FF] text-[#5b8bff]"}`}>
                      {found.role === "admin" ? "Admin" : found.role === "manager" ? "Manager" : "Client"}
                    </span>
                  </div>
                  <div className={`font-mono-term text-xs ${found.license?.status === "suspended" ? "text-[#FFB020]" : found.license?.active ? "text-[#00F593]" : "text-[#888]"}`}>
                    {found.license?.is_staff ? "Staff — full access (no plan)"
                      : found.license?.status === "suspended" ? `Suspended · ${found.license.plan_name || "plan"}`
                      : found.license?.active ? `${found.license.plan_name} · ${found.license.days_left}d left${found.license.auto_renew === false ? " · renewal OFF" : ""}`
                      : found.license?.status === "expired" ? "Expired" : "No plan"}
                  </div>

                  <div className="border-t border-[#222] pt-4 space-y-3">
                    <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Bay / Chanje plan</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <select value={fPlan} onChange={(e) => setFPlan(e.target.value)} data-testid="found-plan-select"
                        className="h-11 w-full rounded-sm bg-[#0A0A0A] border border-[#222] text-white text-sm px-3 outline-none">
                        <option value="">Chwazi yon plan</option>
                        {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — ${p.price} / {p.days}d</option>)}
                      </select>
                      <Input type="number" value={fDays} onChange={(e) => setFDays(e.target.value)} data-testid="found-days-input"
                        placeholder="Jou (opsyonèl)" className="bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
                    </div>
                    <Button onClick={fGrant} data-testid="found-grant-button" disabled={busy === "f-grant"}
                      className="h-10 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] font-semibold w-full sm:w-auto px-6 justify-center">
                      {busy === "f-grant" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Bay Aksè"}
                    </Button>
                  </div>

                  <div className="border-t border-[#222] pt-4 flex flex-wrap gap-2">
                    {found.license?.status === "suspended"
                      ? <Button onClick={() => fAction("reactivate")} data-testid="found-reactivate" disabled={busy === "f-reactivate"} className="h-10 rounded-sm bg-[#00F593] text-black hover:bg-[#00d980] font-semibold px-4">Reyaktive Aksè</Button>
                      : found.license?.active && <Button onClick={() => fAction("revoke")} data-testid="found-revoke" disabled={busy === "f-revoke"} variant="ghost" className="h-10 rounded-sm text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] px-4">Revoke Aksè</Button>}
                    {found.license?.active && (
                      found.license.auto_renew === false
                        ? <Button onClick={() => fAction("enable-renewal")} data-testid="found-enable-renewal" disabled={busy === "f-enable-renewal"} variant="ghost" className="h-10 rounded-sm text-[#8fb4e6] hover:bg-[#8fb4e6]/10 px-4">Aktive Renouvèlman</Button>
                        : <Button onClick={() => fAction("cancel-renewal")} data-testid="found-cancel-renewal" disabled={busy === "f-cancel-renewal"} variant="ghost" className="h-10 rounded-sm text-[#8fb4e6] hover:bg-[#8fb4e6]/10 px-4">Anile Renouvèlman</Button>
                    )}
                    <Button onClick={fVerify} data-testid="found-verify" disabled={busy === "f-verify"} variant="ghost" className="h-10 rounded-sm text-[#00F593] hover:bg-[#00F593]/10 hover:text-[#00F593] px-4">Verifye (san kòd)</Button>
                    <Button onClick={fReminder} data-testid="found-reminder" disabled={busy === "f-reminder"} variant="ghost" className="h-10 rounded-sm text-[#1C8CFF] hover:bg-[#1C8CFF]/10 hover:text-[#1C8CFF] px-4">
                      {busy === "f-reminder" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="mr-1.5 h-4 w-4" /> Voye Rapèl</>}
                    </Button>
                    <Button onClick={fResend} data-testid="found-resend-creds" disabled={busy === "f-resend"} variant="ghost" className="h-10 rounded-sm text-[#00F593] hover:bg-[#00F593]/10 hover:text-[#00F593] px-4">
                      {busy === "f-resend" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="mr-1.5 h-4 w-4" /> Re-voye Enfo Koneksyon</>}
                    </Button>
                    <Button onClick={() => { setComposeTo(found.email); toast.success(`Imèl "${found.email}" pare pou voye dokiman anba a`); }} data-testid="found-send-doc" variant="ghost" className="h-10 rounded-sm text-[#1C8CFF] hover:bg-[#1C8CFF]/10 hover:text-[#1C8CFF] px-4">
                      <Paperclip className="mr-1.5 h-4 w-4" /> Voye Dokiman
                    </Button>
                  </div>

                  <div className="border-t border-[#222] pt-4 space-y-2">
                    <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Wòl staf</label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <select value={fRole} onChange={(e) => setFRole(e.target.value)} data-testid="found-role-select"
                        className="h-11 w-full sm:flex-1 rounded-sm bg-[#0A0A0A] border border-[#222] text-white text-sm px-3 outline-none">
                        <option value="client">Client (reset)</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                        {isCEO && <option value="mentor">Mentor</option>}
                      </select>
                      <Button onClick={fSetRole} data-testid="found-role-button" disabled={busy === "f-role"}
                        className="h-11 rounded-sm bg-[#0057FF] text-white hover:bg-[#0047d6] font-semibold px-6">
                        {busy === "f-role" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mete Wòl"}
                      </Button>
                    </div>
                    <p className="text-[11px] text-[#666] leading-relaxed">Staff (Manager/Admin) jwenn aksè konplè + panèl admin, san plan abònman.</p>
                  </div>

                  {isCEO && (
                    <div className="border-t border-[#FF3B30]/30 pt-4 space-y-2">
                      <label className="font-mono-term text-xs uppercase tracking-widest text-[#FF3B30]">Zòn Danje</label>
                      <p className="text-[11px] text-[#888] leading-relaxed">Efase kont sa a nèt ansanm ak tout done l (pèman, komisyon, kont MT5). Sèvi ak sa pou retire yon kont ki double oswa yon fo kont tès, pou w ka reyitilize imèl la.</p>
                      <Button onClick={fDelete} data-testid="found-delete-button" disabled={busy === "f-delete"}
                        variant="ghost" className="h-10 rounded-sm text-[#FF3B30] border border-[#FF3B30]/40 hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] px-4">
                        {busy === "f-delete" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1.5 h-4 w-4" /> Efase Kont Nèt</>}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="max-w-2xl mt-5">
              <SendEmailCard presetTo={composeTo} />
            </div>
          </TabsContent>

          <TabsContent value="create" className="mt-5">
            <div className="border border-[#1C8CFF]/40 bg-[#0F0F0F] p-6 max-w-2xl space-y-4 mb-5" data-testid="create-admin-card">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-[#1C8CFF] flex items-center justify-center"><ShieldCheck className="h-5 w-5 text-white" /></div>
                <div>
                  <div className="font-heading font-semibold">Create Admin (email only)</div>
                  <div className="font-mono-term text-xs text-[#888]">Kreye yon nouvo admin ak sèlman yon email — li resevwa yon envitasyon pou mete pwòp modpas li</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Email *</label>
                  <Input type="email" value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)} data-testid="new-admin-email"
                    placeholder="newadmin@email.com" className="mt-2 bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
                </div>
                <div>
                  <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Name (opsyonèl)</label>
                  <Input value={newAdminName} onChange={(e) => setNewAdminName(e.target.value)} data-testid="new-admin-name"
                    placeholder="Admin name" className="mt-2 bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
                </div>
              </div>
              <Button onClick={createAdmin} data-testid="create-admin-button" disabled={busy === "create-admin"}
                className="h-11 rounded-sm bg-[#1C8CFF] text-white hover:bg-[#0f7ae6] font-semibold px-6 w-full sm:w-auto">
                {busy === "create-admin" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="mr-1.5 h-4 w-4" /> Send Admin Invite</>}
              </Button>
              <p className="text-[11px] text-[#666] leading-relaxed">Nouvo admin nan jwenn aksè konplè + panèl admin, renouvèlman otomatik chak 3 mwa, san okenn plan abònman.</p>
            </div>

            <div className="border border-[#222] bg-[#0F0F0F] p-6 max-w-2xl space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-white flex items-center justify-center"><KeyRound className="h-5 w-5 text-black" /></div>
                <div>
                  <div className="font-heading font-semibold">Create Member</div>
                  <div className="font-mono-term text-xs text-[#888]">Creates an active account instantly — no email code, no payment</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Full name</label>
                  <Input value={member.name} onChange={(e) => setMember({ ...member, name: e.target.value })} data-testid="member-name"
                    placeholder="David Napoleon" className="mt-2 bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
                </div>
                <div>
                  <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Username (letters only)</label>
                  <Input value={member.username} onChange={(e) => setMember({ ...member, username: e.target.value.replace(/[^a-zA-Z]/g, "") })} data-testid="member-username"
                    placeholder="davidnapolleon" className="mt-2 bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
                </div>
                <div>
                  <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Email</label>
                  <Input type="email" value={member.email} onChange={(e) => setMember({ ...member, email: e.target.value })} data-testid="member-email"
                    placeholder="client@email.com" className="mt-2 bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
                </div>
                <div>
                  <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Phone number</label>
                  <Input value={member.phone} onChange={(e) => setMember({ ...member, phone: e.target.value })} data-testid="member-phone"
                    placeholder="+509 3700 0000" className="mt-2 bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
                </div>
                <div>
                  <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Password</label>
                  <Input type="password" value={member.password} onChange={(e) => setMember({ ...member, password: e.target.value })} data-testid="member-password"
                    placeholder="••••••••" className="mt-2 bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
                </div>
                <div>
                  <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Confirm password</label>
                  <Input type="password" value={member.confirm} onChange={(e) => setMember({ ...member, confirm: e.target.value })} data-testid="member-confirm"
                    placeholder="••••••••" className="mt-2 bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
                </div>
              </div>
              <p className="text-[11px] text-[#666] leading-relaxed">
                Password: at least 6 characters, one uppercase (A-Z), one lowercase (a-z) and one special character ($ ? @ ! ...).
              </p>
              <div>
                <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Access type</label>
                <div className={`mt-2 grid gap-2 ${isCEO ? "grid-cols-4" : "grid-cols-3"}`}>
                  {[{ id: "client", label: "Client" }, { id: "manager", label: "Manager" }, { id: "admin", label: "Admin" }, ...(isCEO ? [{ id: "mentor", label: "Mentor" }] : [])].map((r) => (
                    <button key={r.id} type="button" onClick={() => setMember({ ...member, role: r.id })}
                      data-testid={`member-role-${r.id}`}
                      className={`h-10 rounded-sm border text-sm font-semibold transition-colors ${member.role === r.id ? "border-[#00F593] bg-[#00F593]/10 text-[#00F593]" : "border-[#222] text-[#888] hover:border-[#444]"}`}>
                      {r.label}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-[#666] mt-1.5">
                  {member.role === "client"
                    ? "Client: gets a subscription plan badge (e.g. Elite) and full app access."
                    : member.role === "mentor"
                      ? "Mentor: full access + Signal Composer, Mentor Room & Client Chats. Earns a referral commission."
                      : `Staff: gets a "${member.role === "admin" ? "Admin" : "Manager"}" role badge (no subscription), full access + admin panel. Not a client.`}
                </p>
              </div>
              {member.role === "client" && (
                <div>
                  <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Plan (renews every 30 days)</label>
                  <select value={member.plan_id} onChange={(e) => setMember({ ...member, plan_id: e.target.value })}
                    data-testid="member-plan-select"
                    className="mt-2 h-11 w-full rounded-sm bg-[#0A0A0A] border border-[#222] text-white text-sm px-3 outline-none">
                    {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — ${p.price} / {p.days}d</option>)}
                  </select>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={createMember} data-testid="create-member-button" disabled={memberBusy}
                  className="h-11 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] font-semibold px-6 w-full sm:w-auto">
                  {memberBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
                </Button>
                <Button onClick={inviteMember} data-testid="invite-member-button" disabled={memberBusy}
                  variant="outline"
                  className="h-11 rounded-sm border-[#1C8CFF] bg-transparent text-[#1C8CFF] hover:bg-[#1C8CFF]/10 font-semibold px-6 w-full sm:w-auto">
                  {memberBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Invite Email"}
                </Button>
              </div>
              <p className="text-[11px] text-[#666] leading-relaxed">
                <b className="text-[#8fb4e6]">Send Invite Email</b> only needs the name, email + plan/role above — the client
                receives a link to set their OWN password, username &amp; phone. No password needed here for invites.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="team" className="mt-5" data-testid="team-tab">
            <div className="border border-[#1C8CFF]/40 bg-[#0F0F0F] p-6 max-w-3xl space-y-4 mb-6">
              <div>
                <div className="font-heading font-semibold text-lg">Assign a Position</div>
                <div className="font-mono-term text-xs text-[#888]">Bay yon manm ekip la youn nan tit administratif yo. Tit la ap parèt sou kont li.</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Member email *</label>
                  <Input type="email" value={assignEmail} onChange={(e) => setAssignEmail(e.target.value)} data-testid="assign-email"
                    placeholder="member@email.com" className="mt-2 bg-[#0A0A0A] border-[#222] rounded-sm h-11" />
                </div>
                <div>
                  <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Access level</label>
                  <select value={assignRole} onChange={(e) => setAssignRole(e.target.value)} data-testid="assign-role"
                    className="mt-2 w-full bg-[#0A0A0A] border border-[#222] rounded-sm h-11 px-3 text-sm">
                    <option value="admin">Admin — full control</option>
                    <option value="manager">Manager — staff access</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Position *</label>
                <select value={assignTitle} onChange={(e) => setAssignTitle(e.target.value)} data-testid="assign-title"
                  className="mt-2 w-full bg-[#0A0A0A] border border-[#222] rounded-sm h-11 px-3 text-sm">
                  <option value="">— Choose a position —</option>
                  {teamPositions.map((p) => <option key={p.id} value={p.title}>{p.title} · {p.area}</option>)}
                </select>
                {assignTitle && (
                  <p className="mt-2 text-[11px] text-[#8fb4e6] leading-relaxed">
                    {teamPositions.find((p) => p.title === assignTitle)?.description}
                  </p>
                )}
              </div>
              <Button onClick={assignPosition} data-testid="assign-position-button" disabled={busy === "assign-position"}
                className="h-11 rounded-sm bg-[#1C8CFF] text-white hover:bg-[#0f7ae6] font-semibold px-6">
                {busy === "assign-position" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Assign Position"}
              </Button>
            </div>

            <div className="mb-3"><span className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Current Staff</span></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {teamStaff.length === 0 && <div className="text-sm text-[#666]">No staff members yet.</div>}
              {teamStaff.map((s) => (
                <div key={s.id} className="border border-[#222] bg-[#0F0F0F] p-4 flex items-center justify-between gap-3" data-testid={`staff-row-${s.email}`}>
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-sm truncate">{s.name || s.email}</div>
                    <div className="font-mono-term text-xs text-[#888] truncate">{s.email}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] font-mono-term uppercase tracking-widest px-2.5 py-1 rounded-sm border ${s.license?.status === "suspended" ? "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#1C8CFF]/15 text-[#1C8CFF] border-[#1C8CFF]/40"}`}>
                      {s.license?.status === "suspended" ? "Suspended" : (s.staff_title || (s.role === "admin" ? "Admin" : s.role === "mentor" ? "Mentor" : "Manager"))}
                    </span>
                    {isCEO && !s.is_ceo && (
                      s.license?.status === "suspended"
                        ? <Button size="sm" onClick={() => staffSuspend(s.id, false)} disabled={busy === "staff-" + s.id} data-testid={`staff-reactivate-${s.email}`} className="h-8 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] text-xs font-semibold">Reyaktive</Button>
                        : <Button size="sm" variant="ghost" onClick={() => staffSuspend(s.id, true)} disabled={busy === "staff-" + s.id} data-testid={`staff-suspend-${s.email}`} className="h-8 rounded-sm text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] text-xs">Sispann</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-3"><span className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Position Directory</span></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {teamPositions.map((p, i) => (
                <div key={p.id} className="border border-[#222] bg-[#0F0F0F] p-4" data-testid={`position-${p.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono-term text-xs text-[#555]">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-heading font-semibold text-sm">{p.title}</span>
                  </div>
                  <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#1C8CFF] mb-2">{p.area}</div>
                  <p className="text-[11px] text-[#999] leading-relaxed">{p.description}</p>
                </div>
              ))}
            </div>
          </TabsContent>


          <TabsContent value="tickets" className="mt-5">
            <div className="border border-[#222] bg-[#0F0F0F]">
              {tickets.length === 0 ? <div className="p-10 text-center text-[#888]">No tickets yet.</div> :
                tickets.map((t) => (
                  <div key={t.id} data-testid={`admin-ticket-${t.id}`} className="px-5 py-4 border-b border-[#222]">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-heading font-semibold text-sm">{t.category}</span>
                          <span className={`font-mono-term text-[10px] uppercase tracking-widest px-2 py-0.5 ${t.status === "resolved" ? "text-[#00F593] bg-[#00F593]/10" : "text-[#f5a623] bg-[#f5a623]/10"}`}>{t.status}</span>
                        </div>
                        <div className="font-mono-term text-xs text-[#888] mt-1">{t.user_name} · {t.user_email}{t.username ? ` · @${t.username}` : ""}</div>
                        <p className="text-sm text-[#ccc] mt-2 whitespace-pre-wrap">{t.message}</p>
                        {t.reply && (
                          <div className="mt-2 pl-3 border-l-2 border-[#333]">
                            <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#666]">Your reply</div>
                            <p className="text-sm mt-0.5" style={{ color: "#8fb4e6" }}>{t.reply}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" onClick={() => { setReplyFor(t); setReplyText(t.reply || ""); }} data-testid={`ticket-reply-${t.id}`} className="h-9 rounded-sm bg-white text-black hover:bg-[#e5e5e5] font-semibold"><MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Reply</Button>
                        {t.status !== "resolved" && (
                          <Button size="sm" variant="outline" onClick={() => resolveTicket(t.id)} data-testid={`ticket-resolve-${t.id}`} disabled={busy === t.id + "resolve"} className="h-9 rounded-sm border-[#00F593]/40 text-[#00F593] bg-transparent hover:bg-[#00F593]/10"><Check className="mr-1 h-4 w-4" /> Resolve</Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => deleteTicket(t.id)} data-testid={`ticket-delete-${t.id}`} disabled={busy === t.id + "delete"} className="h-9 rounded-sm text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30]"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </TabsContent>

          <TabsContent value="metaapi" className="mt-5">
            <div className="border border-[#222] bg-[#0F0F0F] p-6 max-w-2xl">
              <div className="flex items-center gap-3 mb-1">
                <div className="h-10 w-10 bg-white flex items-center justify-center"><KeyRound className="h-5 w-5 text-black" /></div>
                <div>
                  <div className="font-heading font-semibold">MetaAPI Connection</div>
                  <div className="font-mono-term text-xs text-[#888]">Live MT5 data provider token</div>
                </div>
              </div>

              <div className="flex items-center gap-2 my-5" data-testid="metaapi-status">
                {config?.metaapi_configured ? (
                  <><CheckCircle2 className="h-4 w-4 text-[#00F593]" /><span className="text-sm text-[#00F593] font-semibold">Configured</span></>
                ) : (
                  <><CircleAlert className="h-4 w-4 text-[#f5a623]" /><span className="text-sm text-[#f5a623] font-semibold">Not configured</span></>
                )}
                {config?.metaapi_token_masked && (
                  <span className="font-mono-term text-xs text-[#888] ml-2">{config.metaapi_token_masked}</span>
                )}
                {config?.updated_at && (
                  <span className="font-mono-term text-[10px] text-[#555] ml-auto">updated {new Date(config.updated_at).toLocaleDateString()}</span>
                )}
              </div>

              <label className="font-mono-term text-xs uppercase tracking-widest text-[#888]">MetaAPI Token</label>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 flex items-center bg-[#0A0A0A] border border-[#222] rounded-sm h-11">
                  <input data-testid="metaapi-token-input" type={showToken ? "text" : "password"} value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    placeholder={config?.metaapi_configured ? "Paste a new token to replace the current one" : "Paste your MetaAPI token here"}
                    className="flex-1 bg-transparent px-3 h-full outline-none text-white text-sm placeholder:text-[#555]" />
                  <button type="button" onClick={() => setShowToken((s) => !s)} data-testid="metaapi-token-toggle"
                    className="px-3 text-[#888] hover:text-white">
                    {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <label className="font-mono-term text-xs uppercase tracking-widest text-[#888] block mt-4">Region</label>
              <select value={region} onChange={(e) => setRegion(e.target.value)}
                data-testid="metaapi-region-select"
                className="mt-2 h-11 w-56 rounded-sm bg-[#0A0A0A] border border-[#222] text-white text-sm px-3 outline-none">
                {["new-york", "london", "singapore", "tokyo", "mumbai"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>

              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mt-6">
                <Button onClick={saveConfig} data-testid="metaapi-save-button" disabled={busy === "config"}
                  className="h-11 rounded-sm bg-white text-black hover:bg-[#e5e5e5] font-semibold px-6 w-full sm:w-auto justify-center">
                  {busy === "config" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Token"}
                </Button>
                {config?.metaapi_configured && (
                  <Button onClick={testConnection} data-testid="metaapi-test-button" disabled={busy === "test"} variant="outline"
                    className="h-11 rounded-sm border-[#00F593]/40 text-[#00F593] bg-transparent hover:bg-[#00F593]/10 w-full sm:w-auto justify-center">
                    {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Activity className="mr-1.5 h-4 w-4" /> Test Connection</>}
                  </Button>
                )}
                {config?.metaapi_configured && (
                  <Button onClick={clearConfig} data-testid="metaapi-clear-button" disabled={busy === "clearconfig"} variant="ghost"
                    className="h-11 rounded-sm text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] w-full sm:w-auto justify-center">
                    <Trash2 className="mr-1.5 h-4 w-4" /> Remove
                  </Button>
                )}
              </div>

              {testResult && (
                <div className="mt-5 border border-[#222] rounded-sm overflow-hidden" data-testid="metaapi-test-result">
                  <div className="px-4 py-2.5 bg-[#0A0A0A] border-b border-[#222] font-mono-term text-xs uppercase tracking-widest text-[#888]">
                    {testResult.count} MT5 account(s) on your MetaAPI
                  </div>
                  {testResult.accounts.map((a) => (
                    <div key={a.id} className="flex items-center justify-between px-4 py-3 border-b border-[#161616]">
                      <div>
                        <div className="font-heading font-semibold text-sm">{a.login} · {a.server}</div>
                        <div className="font-mono-term text-xs text-[#666]">{a.region}</div>
                      </div>
                      <span className={`font-mono-term text-[10px] uppercase tracking-widest px-2 py-0.5 ${a.connection_status === "CONNECTED" ? "text-[#00F593] bg-[#00F593]/10" : "text-[#f5a623] bg-[#f5a623]/10"}`}>{a.connection_status}</span>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs text-[#666] mt-5 leading-relaxed">
                Get your token at <span className="text-[#8fb4e6]">app.metaapi.cloud → API Access</span>. It is stored securely and used to sync live MT5 balances, positions and quotes. When a client connects an MT5 account whose login+server matches one here, live data is shown automatically.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="notify" className="mt-5">
            <div className="border border-[#222] bg-[#0F0F0F] p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-[#8fb4e6]" />
                <h3 className="font-heading font-semibold text-white">Broadcast Push Notification</h3>
              </div>
              <p className="text-xs text-[#888]">Send a push notification to every subscribed client (e.g. "New strategy available").</p>
              <div>
                <label className="text-xs text-[#888]">Title</label>
                <Input data-testid="broadcast-title-input" value={bcast.title}
                  onChange={(e) => setBcast((s) => ({ ...s, title: e.target.value }))}
                  placeholder="New strategy available" className="mt-1 bg-[#0A0A0A] border-[#222] rounded-sm" />
              </div>
              <div>
                <label className="text-xs text-[#888]">Message</label>
                <Textarea data-testid="broadcast-message-input" value={bcast.message}
                  onChange={(e) => setBcast((s) => ({ ...s, message: e.target.value }))}
                  placeholder="Smart Scalper V2.0 is live. Open the app and start trading." rows={3}
                  className="mt-1 bg-[#0A0A0A] border-[#222] rounded-sm" />
              </div>
              <Button data-testid="send-broadcast-button" onClick={sendBroadcast} disabled={bcastBusy}
                className="rounded-sm bg-white text-black hover:bg-white/90 font-semibold">
                {bcastBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Send to all clients</>}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="cpi" className="mt-5">
            <AdminCpiControl />
          </TabsContent>

          <TabsContent value="protection" className="mt-5">
            <AdminProtectionDefaults />
          </TabsContent>

          <TabsContent value="myaccount" className="mt-5">
            <MentorAccount />
          </TabsContent>

          <TabsContent value="branding" className="mt-5">
            <div className="space-y-5">
              <GlobalBotAvatar />
              <BrandingTab />
            </div>
          </TabsContent>

          <TabsContent value="apikeys" className="mt-5">
            <ApiKeysTab />
          </TabsContent>

          <TabsContent value="livesupport" className="mt-5">
            <LiveSupportInbox />
          </TabsContent>

          <TabsContent value="guide" className="mt-5">
            <GuideTab canEdit={isCEO} />
          </TabsContent>


          <TabsContent value="partners" className="mt-5">
            <PartnersTab />
          </TabsContent>

          <TabsContent value="attendance" className="mt-5">
            <AttendanceTab />
          </TabsContent>

          <TabsContent value="signals" className="mt-5">
            <SignalsTab />
          </TabsContent>

          <TabsContent value="stafflog" className="mt-5">
            <StaffAuditTab />
          </TabsContent>

          {showMentorArea && (
            <TabsContent value="mstats" className="mt-5">
              <MentorStats />
            </TabsContent>
          )}
          {showMentorArea && (
            <TabsContent value="composer" className="mt-5">
              <MentorSignalComposer />
            </TabsContent>
          )}
          {showMentorArea && (
            <TabsContent value="mroom" className="mt-5">
              <MentorRoom />
            </TabsContent>
          )}
          {showMentorArea && (
            <TabsContent value="mchats" className="mt-5">
              <MentorClientChats />
            </TabsContent>
          )}
          {isCEO && (
            <TabsContent value="mentors" className="mt-5">
              <MentorsTab />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <Dialog open={!!replyFor} onOpenChange={(o) => { if (!o) { setReplyFor(null); setReplyText(""); } }}>
        <DialogContent className="bg-[#0F0F0F] border-[#222] text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Reply to {replyFor?.user_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-sm border border-[#222] bg-[#0A0A0A] p-3">
              <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#666]">{replyFor?.category}</div>
              <p className="text-sm text-[#ccc] mt-1 whitespace-pre-wrap">{replyFor?.message}</p>
            </div>
            <Textarea data-testid="admin-reply-input" value={replyText} onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply to the client..."
              className="min-h-[120px] bg-[#0A0A0A] border-[#222] rounded-sm focus-visible:ring-0" />
          </div>
          <DialogFooter>
            <Button data-testid="admin-reply-send" onClick={sendReply} disabled={busy === "reply" || !replyText.trim()}
              className="w-full h-11 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] font-semibold">
              {busy === "reply" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Send Reply & Resolve</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!proof} onOpenChange={(o) => !o && setProof(null)}>
        <DialogContent className="bg-[#0F0F0F] border-[#222] text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Payment Proof</DialogTitle></DialogHeader>
          {proof && <img src={proof} alt="proof" className="w-full border border-[#222]" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
